import type { GetStaticProps, NextPage } from "next";
import Image from "next/legacy/image";
import { useForm } from "react-hook-form";
import { registerLocale } from "react-datepicker";
import enNZ from "date-fns/locale/en-NZ";
import { useFormspark } from "@formspark/use-formspark";
import Page from "@/components/Page";
import { useCallback, useState } from "react";

// import {
//   CakeTextInput as TextInput,
//   CakeSelect as Select,
// } from "@/components/rhf-components";

registerLocale("en-NZ", enNZ);

import { ContactUsState } from "types";
import TextInput from "@/components/TextInput";
import Modal from "@/components/Dialog";
import Thanks from "@/components/Thanks";
import { SocialIcon } from "react-social-icons";
import { Heading2, Heading3 } from "@/components/Elements";

const FORM_ID = process.env.NEXT_PUBLIC_FORMSPARK_CONTACT_FORM_ID || "test";

type Status = "ready" | "submitting" | "success" | "error";

const ContactUsForm: NextPage = () => {
  const [submit, submitting] = useFormspark({ formId: FORM_ID });

  const [status, setStatus] = useState<Status>("ready");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactUsState>();

  const onSubmit = useCallback(
    async (data: ContactUsState) => {
      console.log(data);
      // show submitting dialog
      try {
        setStatus("submitting");
        // get a request number
        await submit(data);

        // show success dialog
        // - go to home
        // - make another request
        setStatus("success");
      } catch {
        // show error dialog
        setStatus("error");
      }
    },
    [setStatus, submit]
  );

  const resetForMultiple = () => {
    // only reset the fields that are likely to change
    window.location.reload();
  };

  return (
    <Page title="Cake Request Form - The Cake Detective">
      <Page.Header />
      <Page.Content>
        <div className="lg:mx-8">
          <div className="lg:mx-auto lg:max-w-7xl lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-12">
            <div className="py-8 max-w-xl mx-auto col-start-1 lg:py-32 lg:max-w-none lg:mx-0 lg:px-0 lg:col-start-1">
              <Heading2>The Cake Detective</Heading2>
              <Heading3>Contact Us</Heading3>
              <p className="mt-4 mb-6 text-md text-navy-500">
                You can email us directly at the following address or use the
                form below
              </p>
              <div className="mt-1 mb-12 text-sm space-x-2">
                <SocialIcon
                  url="mailto:info@thecakedetective.com"
                  bgColor="#e46c3e"
                  style={{ width: "32px", height: "32px" }}
                />
                <a
                  href="mailto:info@thecakedetective"
                  className="text-brand-ranga underline"
                >
                  info@thecakedetective.com
                </a>
              </div>
              <form
                noValidate
                className="space-y-8 divide-y divide-gray-200"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="space-y-8 divide-y divide-ranga-200">
                  <div>
                    <div>
                      <h4 className="text-lg leading-6 font-semibold uppercase text-ranga-base tracking-wide">
                        Contact Form
                      </h4>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-start-1 sm:col-end-3">
                        <TextInput
                          label="Name"
                          type="text"
                          required
                          error={errors["name"]?.message}
                          {...register("name", {
                            required: "Please let us know who you are",
                          })}
                        />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-start-1 sm:col-end-3">
                        <TextInput
                          label="Email"
                          type="email"
                          required
                          error={errors["email"]?.message}
                          {...register("email", {
                            required: "An email address is required",
                          })}
                        />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-start-1 sm:col-end-3">
                        <TextInput
                          label="Subject"
                          type="text"
                          required
                          error={errors["subject"]?.message}
                          {...register("subject", {
                            required: "A subject is required",
                          })}
                        />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                      <div className="sm:col-start-1 sm:col-end-3">
                        <label
                          htmlFor="message"
                          className="block text-sm font-medium text-navy-500"
                        >
                          Message *
                        </label>
                        <textarea
                          id="message"
                          rows={4}
                          className="mt-1 shadow-sm focus:ring-teal-500 focus:border-teal-500 block w-full sm:text-sm border-ranga-300 rounded-md"
                          {...register("message", {
                            required: "A message is required",
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div className="flex items-center justify-between">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                      >
                        {submitting ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Thanks title=" Thanks to our generous sponsors" />
        {(submitting || status === "submitting" || status === "success") && (
          <Modal
            initialOpen
            onClose={resetForMultiple}
            closeOnly={true}
            isLoading={submitting || status === "submitting"}
          >
            <span>Thanks for your enquiry, we&apos;ll be in touch</span>
          </Modal>
        )}
      </Page.Content>
      <Page.Footer />
    </Page>
  );
};

export default ContactUsForm;
